<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import Icon from "./Icon.vue";
import {
  api, pln, when,
  type AccessStats, type AdminRun, type AdminUser, type Invoice, type Pricing, type SupportRequest,
} from "../api";
import { href } from "../router";

// Panel admina: co zostało naprawione i za ile (do zafakturowania), zgłoszenia
// do devopsa (godziny → faktura), rejestr faktur, lista klientów.
//
// Faktura powstaje w programie księgowym admina. Tu rejestrujemy numer, kwotę i
// status, żeby klient widział w swoim panelu to samo, co admin.

const props = defineProps<{ pricing: Pricing | null }>();

type Tab = "runs" | "support" | "invoices" | "users" | "access";
const tab = ref<Tab>("runs");
const runs = ref<AdminRun[]>([]);
const requests = ref<SupportRequest[]>([]);
const invoices = ref<Invoice[]>([]);
const users = ref<AdminUser[]>([]);
const access = ref<AccessStats | null>(null);
const accessDays = ref(30);
const err = ref("");
const busy = ref<string | null>(null);

async function load() {
  err.value = "";
  try {
    const [r, s, i, u, a] = await Promise.all([
      api.get<{ runs: AdminRun[] }>("/api/admin/runs"),
      api.get<{ requests: SupportRequest[] }>("/api/admin/support"),
      api.get<{ invoices: Invoice[] }>("/api/admin/invoices"),
      api.get<{ users: AdminUser[] }>("/api/admin/users"),
      api.get<AccessStats>(`/api/admin/access?days=${accessDays.value}`),
    ]);
    runs.value = r.runs;
    requests.value = s.requests;
    invoices.value = i.invoices;
    users.value = u.users;
    access.value = a;
  } catch (e) {
    err.value = (e as Error).message;
  }
}
onMounted(load);

async function loadAccess() {
  try {
    access.value = await api.get<AccessStats>(`/api/admin/access?days=${accessDays.value}`);
  } catch (e) {
    err.value = (e as Error).message;
  }
}

const eventText: Record<string, string> = {
  login_page: "ekran logowania", login_ok: "zalogowano", login_fail: "błędne logowanie",
  register: "rejestracja", verify: "aktywacja konta", site: "strona WWW",
};
const shortUa = (ua: string | null) => {
  if (!ua) return "—";
  const m = /(Chrome|Firefox|Safari|Edg|OPR|curl|bot|Bot|spider)/.exec(ua);
  const os = /(Windows|Mac OS|Android|iPhone|iPad|Linux)/.exec(ua);
  return [os?.[1], m?.[1]].filter(Boolean).join(" / ") || ua.slice(0, 30);
};

const openSupport = computed(() => requests.value.filter((r) => r.status !== "closed").length);
const toInvoice = computed(() =>
  runs.value.filter((r) => r.mode === "quarantine" && r.status !== "running" && r.status !== "failed" && !r.invoice).length,
);

const userHasBilling = (email: string) => users.value.find((u) => u.email === email)?.hasBilling ?? true;

async function invoiceRun(r: AdminRun) {
  if (!userHasBilling(r.userEmail)) {
    alert(`Klient ${r.userEmail} nie uzupełnił danych do faktury. Poproś go o to (zakładka Rozliczenia w jego panelu) — bez tego faktura nie ma nabywcy.`);
    return;
  }
  const suggested = r.price?.amountPln ?? r.pricePln ?? 0;
  const b = r.price?.breakdown ?? {};
  const aiTotal = (r.costUsd ?? 0) + (r.assistCostUsd ?? 0);
  const calc = r.price?.algorithm === "v1"
    ? `AI $${aiTotal.toFixed(2)} × ${b.usdPln ?? "?"} zł × ${b.aiMultiplier ?? "?"} = ${pln(b.aiWithMarginPln ?? 0)} + ${b.baseFeePln ?? "?"} zł opłaty stałej (stary cennik)`
    : `AI $${aiTotal.toFixed(2)} × ${b.usdPln ?? "?"} zł × ${b.aiMultiplier ?? "?"} = ${pln(b.aiWithMarginPln ?? 0)} → ${b.steps ?? 1} × ${b.stepPln ?? 200} zł (progi po ${b.stepPln ?? 200} zł)`;
  const raw = prompt(
    `Faktura za naprawę ${r.domain} dla ${r.userEmail}.\n\n` +
      `Wyliczenie: ${calc} = ${pln(suggested)}\n\n` +
      "Kwota (zł) — zmień, jeśli udzielasz rabatu:",
    suggested.toFixed(2),
  );
  if (raw === null) return;
  const amount = Number(raw.replace(",", "."));
  if (!Number.isFinite(amount) || amount < 0) return alert("Nieprawidłowa kwota.");
  const note = prompt("Uwaga na fakturze (opcjonalnie):", "") ?? "";
  busy.value = r.runId;
  try {
    await api.post("/api/admin/invoices", { kind: "repair", runId: r.runId, amountPln: amount, note });
    await load();
  } catch (e) {
    alert((e as Error).message);
  } finally {
    busy.value = null;
  }
}

/** Odpowiedź klientowi na zgłoszony problem („poprawione — sprawdź ponownie"). Klient dostaje mail. */
async function replyRun(r: AdminRun) {
  const msg = prompt(
    `Odpowiedź dla ${r.userEmail} w sprawie ${r.domain}.\n\n` +
      (r.lastProblem ? `Zgłoszony problem: „${r.lastProblem}”\n\n` : "") +
      "Klient dostanie tę treść mailem i prośbę, żeby sprawdził stronę ponownie:",
    "Poprawione — sprawdź proszę stronę jeszcze raz i potwierdź w panelu.",
  );
  if (msg === null || !msg.trim()) return;
  busy.value = r.runId;
  try {
    await api.post(`/api/admin/runs/${encodeURIComponent(r.runId)}/reply`, { message: msg.trim() });
    await load();
  } catch (e) {
    alert((e as Error).message);
  } finally {
    busy.value = null;
  }
}

async function saveSupport(s: SupportRequest) {
  busy.value = s.id;
  try {
    await api.patch(`/api/admin/support/${encodeURIComponent(s.id)}`, {
      status: s.status,
      hours: s.hours,
      adminNote: s.adminNote,
    });
    await load();
  } catch (e) {
    alert((e as Error).message);
  } finally {
    busy.value = null;
  }
}

async function invoiceSupport(s: SupportRequest) {
  if (s.userEmail && !userHasBilling(s.userEmail)) {
    alert(`Klient ${s.userEmail} nie uzupełnił danych do faktury.`);
    return;
  }
  const hourly = props.pricing?.devopsHourlyPln ?? 200;
  const suggested = Math.round(s.hours * hourly * 100) / 100;
  const raw = prompt(
    `Faktura za pomoc devopsa (${s.runDomain ?? "—"}) dla ${s.userEmail}.\n\n` +
      `Wyliczenie: ${s.hours} h × ${hourly} zł = ${pln(suggested)}\n\nKwota (zł):`,
    suggested.toFixed(2),
  );
  if (raw === null) return;
  const amount = Number(raw.replace(",", "."));
  if (!Number.isFinite(amount) || amount < 0) return alert("Nieprawidłowa kwota.");
  busy.value = s.id;
  try {
    await api.post("/api/admin/invoices", { kind: "devops", supportRequestId: s.id, amountPln: amount });
    await load();
  } catch (e) {
    alert((e as Error).message);
  } finally {
    busy.value = null;
  }
}

async function setInvoiceStatus(i: Invoice, status: Invoice["status"]) {
  busy.value = i.id;
  try {
    await api.patch(`/api/admin/invoices/${encodeURIComponent(i.id)}`, { status });
    await load();
  } catch (e) {
    alert((e as Error).message);
  } finally {
    busy.value = null;
  }
}

const statusPill: Record<string, { cls: string; text: string }> = {
  running: { cls: "acc", text: "w toku" },
  done: { cls: "ok", text: "gotowe" },
  warning: { cls: "warn", text: "z uwagami" },
  failed: { cls: "crit", text: "błąd" },
  issued: { cls: "warn", text: "do zapłaty" },
  paid: { cls: "ok", text: "zapłacona" },
  cancelled: { cls: "", text: "anulowana" },
  open: { cls: "crit", text: "nowe" },
  in_progress: { cls: "acc", text: "w realizacji" },
  closed: { cls: "ok", text: "zamknięte" },
};
</script>

<template>
  <div class="admin">
    <div class="tabs">
      <button type="button" :class="{ on: tab === 'runs' }" @click="tab = 'runs'">
        Naprawy <span v-if="toInvoice" class="cnt">{{ toInvoice }} do zafakturowania</span>
      </button>
      <button type="button" :class="{ on: tab === 'support' }" @click="tab = 'support'">
        Zgłoszenia <span v-if="openSupport" class="cnt hot">{{ openSupport }}</span>
      </button>
      <button type="button" :class="{ on: tab === 'invoices' }" @click="tab = 'invoices'">Faktury ({{ invoices.length }})</button>
      <button type="button" :class="{ on: tab === 'users' }" @click="tab = 'users'">Klienci ({{ users.length }})</button>
      <button type="button" :class="{ on: tab === 'access' }" @click="tab = 'access'">
        Odwiedziny <span v-if="access" class="cnt">{{ access.totals.site + access.totals.loginPage }}</span>
      </button>
      <span class="spacer" />
      <button type="button" class="link" @click="load"><Icon name="refresh" /> odśwież</button>
    </div>
    <p v-if="err" class="err">{{ err }}</p>

    <!-- Naprawy -->
    <div v-if="tab === 'runs'" class="card">
      <h2>Naprawy wszystkich klientów</h2>
      <div class="body scroll">
        <table>
          <thead>
            <tr>
              <th>Data</th><th>Klient</th><th>Strona</th><th>Status</th><th>Zagrożenia</th>
              <th>Akcje</th><th>Koszt AI</th><th>Cena</th><th>Klient potwierdził</th><th>Faktura</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in runs" :key="r.runId">
              <td class="muted nowrap">{{ when(r.createdAt) }}</td>
              <td>{{ r.userEmail }}</td>
              <td><b>{{ r.domain }}</b><div class="muted mono small">{{ r.target }}</div></td>
              <td>
                <span class="pill" :class="statusPill[r.status]?.cls">{{ statusPill[r.status]?.text }}</span>
                <div class="muted small">{{ r.mode === "quarantine" ? "naprawa" : "raport" }}</div>
              </td>
              <td><b :class="{ hot: (r.threats ?? 0) > 0 }">{{ r.threats ?? "—" }}</b></td>
              <td class="nowrap"><Icon name="alert" /> {{ r.progress?.actions.quarantined ?? r.quarantined }} &nbsp;<Icon name="wrench" /> {{ r.progress?.actions.repaired ?? r.repaired }}</td>
              <td class="nowrap">
                {{ r.costUsd !== null ? "$" + r.costUsd.toFixed(2) : "—" }}
                <div v-if="r.assistCostUsd > 0" class="muted small">+ czat ${{ r.assistCostUsd.toFixed(2) }}</div>
                <a class="link small" :href="href('naprawy', r.runId)"><Icon name="chat" /> otwórz / czat AI</a>
              </td>
              <td class="nowrap">
                <b v-if="r.price">{{ pln(r.price.amountPln) }}</b><span v-else class="muted">—</span>
                <div v-if="r.price?.algorithm === 'v1'" class="muted small">stary cennik</div>
              </td>
              <td>
                <span v-if="r.feedback === 'ok'" class="okc"><Icon name="check" /> działa</span>
                <span v-else-if="r.feedback === 'broken'" class="critc"><Icon name="x" /> zgłosił problem<template v-if="r.problemCount > 1"> ({{ r.problemCount }}×)</template></span>
                <span v-else class="muted">brak odpowiedzi</span>
                <div v-if="r.siteOk === false" class="critc small">automat: strona nie odpowiada</div>
                <template v-if="r.feedback === 'broken'">
                  <div v-if="r.lastProblem" class="problem small">„{{ r.lastProblem }}”</div>
                  <div class="muted small">{{ r.lastProblemVia === "assist" ? "wybrał asystenta AI" : r.lastProblemVia === "devops" ? "wezwał devopsa" : "bez wyboru drogi" }}</div>
                  <button type="button" class="link small" :disabled="busy === r.runId" @click="replyRun(r)"><Icon name="chat" :size="12" /> odpowiedz klientowi</button>
                </template>
              </td>
              <td class="nowrap">
                <template v-if="r.invoice">
                  <span class="mono">{{ r.invoice.number }}</span>
                  <div><span class="pill" :class="statusPill[r.invoice.status]?.cls">{{ statusPill[r.invoice.status]?.text }}</span></div>
                </template>
                <template v-else-if="r.mode === 'quarantine' && r.status !== 'running' && r.status !== 'failed'">
                  <button type="button" class="small" :disabled="busy === r.runId" @click="invoiceRun(r)"><Icon name="receipt" /> Wystaw fakturę</button>
                  <div v-if="!userHasBilling(r.userEmail)" class="critc small">brak danych nabywcy</div>
                </template>
                <span v-else class="muted">—</span>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="!runs.length" class="empty">Brak runów.</p>
      </div>
    </div>

    <!-- Zgłoszenia -->
    <div v-if="tab === 'support'" class="card">
      <h2>Zgłoszenia do devopsa <span class="muted small">— {{ pricing?.devopsHourlyPln ?? 200 }} zł/h</span></h2>
      <div class="body">
        <p v-if="!requests.length" class="empty">Brak zgłoszeń.</p>
        <div v-for="s in requests" :key="s.id" class="sr" :class="{ closed: s.status === 'closed' }">
          <div class="row" style="justify-content:space-between">
            <span><b>{{ s.runDomain ?? "—" }}</b> • {{ s.userEmail }} <span class="muted">• {{ when(s.createdAt) }}</span></span>
            <span class="pill" :class="statusPill[s.status]?.cls">{{ statusPill[s.status]?.text }}</span>
          </div>
          <p class="msg">„{{ s.message || "(bez opisu)" }}”</p>
          <p v-if="s.runId" class="muted small mono">run: <a :href="href('naprawy', s.runId)">{{ s.runId }}</a> — <a :href="href('naprawy', s.runId)"><Icon name="chat" /> otwórz naprawę i czat AI</a></p>
          <div class="grid">
            <div class="field">
              <label>Status</label>
              <select v-model="s.status">
                <option value="open">nowe</option>
                <option value="in_progress">w realizacji</option>
                <option value="closed">zamknięte</option>
              </select>
            </div>
            <div class="field">
              <label>Godziny pracy</label>
              <input v-model.number="s.hours" type="number" step="0.25" min="0">
            </div>
            <div class="field" style="grid-column: span 2">
              <label>Notatka dla klienta</label>
              <input v-model="s.adminNote" type="text" placeholder="co zrobiono / co ustalono">
            </div>
          </div>
          <div class="row">
            <button type="button" class="sec small" :disabled="busy === s.id" @click="saveSupport(s)"><Icon name="save" /> Zapisz</button>
            <template v-if="s.invoice">
              <span class="muted small">faktura {{ s.invoice.number }} ({{ pln(s.invoice.amountPln) }})</span>
            </template>
            <button v-else type="button" class="small" :disabled="busy === s.id || s.hours <= 0" @click="invoiceSupport(s)">
              <Icon name="receipt" /> Wystaw fakturę ({{ pln(s.hours * (pricing?.devopsHourlyPln ?? 200)) }})
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Faktury -->
    <div v-if="tab === 'invoices'" class="card">
      <h2>Rejestr faktur</h2>
      <div class="body scroll">
        <table>
          <thead><tr><th>Numer</th><th>Nabywca</th><th>Za</th><th>Kwota</th><th>Status</th><th>Wystawiono</th><th></th></tr></thead>
          <tbody>
            <tr v-for="i in invoices" :key="i.id">
              <td class="mono">{{ i.number }}</td>
              <td>
                <template v-if="i.buyer">
                  <b>{{ i.buyer.name }}</b>
                  <div class="muted small">{{ i.buyer.nip ? "NIP " + i.buyer.nip + " • " : "" }}{{ i.buyer.address }}, {{ i.buyer.postalCode }} {{ i.buyer.city }}</div>
                  <div class="muted small">{{ i.buyer.invoiceEmail || i.userEmail }}</div>
                </template>
                <template v-else>{{ i.userEmail }} <span class="muted small">(bez danych nabywcy)</span></template>
              </td>
              <td>{{ i.kind === "repair" ? "naprawa" : "devops" }}<span v-if="i.runDomain" class="muted"> — {{ i.runDomain }}</span><div v-if="i.note" class="muted small">{{ i.note }}</div></td>
              <td><b>{{ pln(i.amountPln) }}</b></td>
              <td><span class="pill" :class="statusPill[i.status]?.cls">{{ statusPill[i.status]?.text }}</span></td>
              <td class="muted nowrap">{{ when(i.issuedAt) }}</td>
              <td class="nowrap">
                <button v-if="i.status === 'issued'" type="button" class="small" :disabled="busy === i.id" @click="setInvoiceStatus(i, 'paid')"><Icon name="check" /> Zapłacona</button>
                <button v-if="i.status !== 'cancelled'" type="button" class="danger" :disabled="busy === i.id" @click="setInvoiceStatus(i, 'cancelled')">Anuluj</button>
                <button v-if="i.status === 'cancelled'" type="button" class="sec small" :disabled="busy === i.id" @click="setInvoiceStatus(i, 'issued')">Przywróć</button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="!invoices.length" class="empty">Brak faktur.</p>
      </div>
    </div>

    <!-- Odwiedziny -->
    <template v-if="tab === 'access' && access">
      <div class="row" style="margin-bottom: var(--s3)">
        <span class="muted small">Okres:</span>
        <select v-model.number="accessDays" style="width:auto" @change="loadAccess">
          <option :value="7">7 dni</option><option :value="30">30 dni</option><option :value="90">90 dni</option><option :value="365">rok</option>
        </select>
      </div>
      <div class="tiles">
        <div class="tile"><b>{{ access.totals.site }}</b><span>odsłon strony WWW i bloga</span></div>
        <div class="tile"><b>{{ access.totals.loginPage }}</b><span>wejść na ekran logowania</span></div>
        <div class="tile"><b>{{ access.totals.loginOk }}</b><span>udanych logowań</span></div>
        <div class="tile"><b :class="{ hot: access.totals.loginFail > 20 }">{{ access.totals.loginFail }}</b><span>nieudanych logowań</span></div>
        <div class="tile"><b>{{ access.totals.register }}</b><span>rejestracji</span></div>
        <div class="tile"><b>{{ access.totals.uniqueIps }}</b><span>unikalnych adresów IP</span></div>
      </div>

      <div class="card">
        <h2>Kto jest / był w systemie (aktywne sesje)</h2>
        <div class="body">
          <p v-if="!access.activeUsers.length" class="empty">Nikt nie ma aktywnej sesji.</p>
          <table v-else>
            <thead><tr><th>Konto</th><th>Ostatnio aktywny</th><th>Sesje</th></tr></thead>
            <tbody>
              <tr v-for="u in access.activeUsers" :key="u.email">
                <td>{{ u.email }}</td><td class="muted nowrap">{{ when(u.lastSeenAt) }}</td><td>{{ u.sessions }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="card">
        <h2>Dzień po dniu</h2>
        <div class="body scroll">
          <p v-if="!access.days.length" class="empty">Brak zdarzeń w tym okresie — nikt jeszcze nie wchodził.</p>
          <table v-else>
            <thead><tr><th>Dzień</th><th>Strona WWW</th><th>Ekran logowania</th><th>Logowania</th><th>Błędne</th><th>Rejestracje</th><th>Unikalne IP</th></tr></thead>
            <tbody>
              <tr v-for="d in [...access.days].reverse()" :key="d.day">
                <td class="mono">{{ d.day }}</td><td>{{ d.site }}</td><td>{{ d.loginPage }}</td><td>{{ d.loginOk }}</td>
                <td :class="{ critc: d.loginFail > 10 }">{{ d.loginFail }}</td><td>{{ d.register }}</td><td>{{ d.uniqueIps }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="access.topPaths.length" class="card">
        <h2>Najczęściej odwiedzane podstrony (cleanops.pl)</h2>
        <div class="body">
          <table>
            <thead><tr><th>Adres</th><th>Odsłony</th></tr></thead>
            <tbody><tr v-for="p in access.topPaths" :key="p.path"><td class="mono">{{ p.path }}</td><td>{{ p.views }}</td></tr></tbody>
          </table>
        </div>
      </div>

      <div class="card">
        <h2>Ostatnie zdarzenia (200)</h2>
        <div class="body scroll">
          <table>
            <thead><tr><th>Kiedy</th><th>Zdarzenie</th><th>Konto</th><th>IP</th><th>Przeglądarka</th><th>Strona / źródło</th></tr></thead>
            <tbody>
              <tr v-for="e in access.recent" :key="e.id">
                <td class="muted nowrap">{{ when(e.createdAt) }}</td>
                <td><span class="pill" :class="e.event === 'login_fail' ? 'crit' : e.event === 'login_ok' ? 'ok' : e.event === 'site' ? '' : 'acc'">{{ eventText[e.event] ?? e.event }}</span></td>
                <td>{{ e.userEmail ?? "—" }}</td>
                <td class="mono">{{ e.ip ?? "—" }}</td>
                <td class="muted small">{{ shortUa(e.userAgent) }}</td>
                <td class="muted small mono">{{ e.path ?? e.referrer ?? "—" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- Klienci -->
    <div v-if="tab === 'users'" class="card">
      <h2>Klienci</h2>
      <div class="body scroll">
        <table>
          <thead><tr><th>E-mail</th><th>Konto</th><th>Dane do faktury</th><th>Założone</th><th>Runy</th><th>Otwarte zgłoszenia</th></tr></thead>
          <tbody>
            <tr v-for="u in users" :key="u.id">
              <td>{{ u.email }} <span v-if="u.isAdmin" class="pill acc">admin</span></td>
              <td><span class="pill" :class="u.emailVerified ? 'ok' : 'warn'">{{ u.emailVerified ? "aktywne" : "nieaktywowane" }}</span></td>
              <td><span class="pill" :class="u.hasBilling ? 'ok' : 'crit'">{{ u.hasBilling ? "są" : "brak" }}</span></td>
              <td class="muted nowrap">{{ when(u.createdAt) }}</td>
              <td>{{ u.runs }}</td>
              <td><b :class="{ hot: u.openSupport > 0 }">{{ u.openSupport }}</b></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.admin { padding: var(--s4); max-width: 1500px; margin: 0 auto; }
.tabs { display: flex; gap: var(--s2); align-items: center; margin-bottom: var(--s4); flex-wrap: wrap; }
.tabs > button:not(.link) { background: var(--panel2); color: var(--mut); border: 1px solid var(--bd); font-weight: 600; }
.tabs > button.on { background: var(--acc); color: var(--acc-fg); border-color: transparent; }
.cnt { font-size: 10px; background: var(--panel3); color: var(--fg); border-radius: 99px; padding: 1px 7px; margin-left: 4px; }
.cnt.hot { background: var(--crit); color: #fff; }
.scroll { overflow-x: auto; }
.small { font-size: 11px; }
.nowrap { white-space: nowrap; }
.hot { color: var(--crit); }
.problem { max-width: 260px; white-space: pre-wrap; color: var(--fg); margin-top: 4px; }
.okc { color: var(--ok); } .critc { color: var(--crit); }
.sr { border: 1px solid var(--bd); border-radius: var(--r2); padding: var(--s3); background: var(--panel2); margin-bottom: var(--s3); font-size: 12px; }
.sr.closed { opacity: .7; }
.msg { margin: var(--s2) 0; font-size: 13px; }
.grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--s2) var(--s3); margin: var(--s2) 0; }
.grid .field { margin: 0; }
.tiles { display: grid; grid-template-columns: repeat(6, 1fr); gap: var(--s3); margin-bottom: var(--s4); }
@media (max-width: 900px) { .tiles { grid-template-columns: repeat(3, 1fr); } }
.tile { background: var(--panel); border: 1px solid var(--bd); border-radius: var(--r3); padding: var(--s3) var(--s4); box-shadow: var(--shadow); }
.tile b { display: block; font-size: 26px; font-weight: 750; letter-spacing: -.02em; }
.tile span { font-size: 12px; color: var(--mut); }
</style>
